import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import ArticleCard from '@/components/feature/ArticleCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { articles, getArticleById } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  const related = useMemo(() => {
    if (!article) return [];
    return articles.filter((item) => item.id !== article.id).slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        articleSection: article.category,
        keywords: article.tags.join(','),
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: 'zh-CN',
        author: { '@type': 'Organization', name: '黑料今日' },
        publisher: { '@type': 'Organization', name: '黑料今日' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-today.example/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.category,
            item: 'https://heiliao-today.example/ranking',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: `https://heiliao-today.example/article/${article.id}`,
          },
        ],
      },
    ];
  }, [article]);

  usePageMeta({
    title: article
      ? `${article.title} - 黑料今日热点盘点`
      : '文章未找到 - 黑料今日',
    description: article
      ? `${article.summary} 本文属于「${article.category}」，约 ${article.readMinutes} 分钟可读完，含话题来龙去脉与不同声音的整理。`
      : '未找到对应的热点盘点内容，可返回首页查看今日热搜榜单。',
    keywords: article
      ? article.tags.join(',')
      : '黑料今日,今日热搜盘点,热门话题',
    path: article ? `/article/${article.id}` : '/',
    type: 'article',
    jsonLd,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <SiteNavbar />
        <main className="flex-1 pt-16 flex items-center justify-center px-4">
          <div className="text-center py-24">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-3xl text-foreground-400">
              <i className="ri-file-unknow-line" aria-hidden="true"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇盘点不见了</h1>
            <p className="mt-3 text-sm text-foreground-600">可能链接已失效，去热搜榜看看今天还有什么热点吧。</p>
            <Link
              to="/ranking"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
            >
              <i className="ri-fire-line" aria-hidden="true"></i>
              查看今日热搜榜
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteNavbar />

      <main className="flex-1 pt-16">
        <article>
          <header className="w-full bg-secondary-950">
            <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
              <nav aria-label="面包屑导航" className="mb-6">
                <ol className="flex flex-wrap items-center gap-2 text-xs text-secondary-300/80">
                  <li>
                    <Link to="/" className="hover:text-accent-300 transition-colors">
                      首页
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <i className="ri-arrow-right-s-line"></i>
                  </li>
                  <li>
                    <Link to="/ranking" className="hover:text-accent-300 transition-colors">
                      {article.category}
                    </Link>
                  </li>
                  <li aria-hidden="true">
                    <i className="ri-arrow-right-s-line"></i>
                  </li>
                  <li className="text-accent-300 line-clamp-2">{article.title}</li>
                </ol>
              </nav>

              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs mb-4">
                <i className="ri-bookmark-3-line" aria-hidden="true"></i>
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-snug max-w-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-100/75 leading-7 max-w-3xl">{article.summary}</p>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-background-100/60">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  约 {article.readMinutes} 分钟
                </span>
                <span className="flex items-center gap-1.5 text-accent-300">
                  <i className="ri-fire-fill" aria-hidden="true"></i>
                  热度 {article.heat.toLocaleString()}
                </span>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 lg:gap-14">
              <div className="min-w-0">
                <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-100 mb-8">
                  <img
                    src={article.cover}
                    alt={`${article.title} 话题配图`}
                    title={`${article.title} · 黑料今日热点盘点`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="max-w-3xl">
                  {article.content.map((section) => (
                    <section key={section.heading} className="mb-8">
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-start gap-3">
                        <span className="w-1.5 h-6 rounded-full bg-primary-500 mt-1.5 shrink-0" aria-hidden="true"></span>
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((paragraph, index) => (
                        <p key={index} className="text-base text-foreground-700 leading-8 mb-4">
                          {paragraph}
                        </p>
                      ))}
                    </section>
                  ))}

                  <div className="mt-10 p-5 rounded-lg bg-accent-50 border border-accent-200">
                    <p className="text-sm text-foreground-700 leading-7 flex gap-3">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center text-accent-600">
                        <i className="ri-information-line" aria-hidden="true"></i>
                      </span>
                      本文为公开网络讨论的整理与盘点，仅代表网友观点，不构成对任何人、任何事件的事实认定。
                      请理性阅读，独立判断。
                    </p>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-foreground-500 mr-1">话题标签</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="lg:sticky lg:top-24 h-fit">
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-fire-line" aria-hidden="true"></i>
                    </span>
                    同一天的热点
                  </h2>
                  <ul className="space-y-4">
                    {related.map((item, index) => (
                      <li key={item.id}>
                        <Link to={`/article/${item.id}`} className="group flex gap-3">
                          <span
                            className={`w-6 h-6 shrink-0 rounded-md flex items-center justify-center text-xs font-bold ${
                              index === 0
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-200 text-foreground-600'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-foreground-800 group-hover:text-primary-700 transition-colors leading-6 line-clamp-2">
                              {item.title}
                            </span>
                            <span className="mt-1 text-[11px] text-foreground-500 flex items-center gap-2">
                              <span>{item.category}</span>
                              <span className="flex items-center gap-0.5">
                                <i className="ri-fire-line" aria-hidden="true"></i>
                                {item.heat.toLocaleString()}
                              </span>
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/ranking"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-foreground-950 text-sm text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-colors whitespace-nowrap"
                  >
                    查看完整榜单
                    <i className="ri-arrow-right-line" aria-hidden="true"></i>
                  </Link>
                </div>
              </aside>
            </div>

            <section className="mt-14 pt-10 border-t border-background-200">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">你可能也想看</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </section>
          </div>
        </article>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}